import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { ColorModeProvider } from './contexts/ColorModeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import MainLayout from './layouts/MainLayout';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import ConfirmEmailPage from './pages/auth/ConfirmEmailPage';
import InstructorDashboard from './pages/instructor/InstructorDashboard';
import AddAvailabilityForm from './pages/instructor/AddAvailabilityForm';
import StudentManagement from './pages/instructor/StudentManagement';
import SubscribePage from './pages/instructor/SubscribePage';
import WhatsAppSettings from './pages/instructor/WhatsAppSettings';
import PaymentPage from './pages/payment/PaymentPage';
import StudentDashboard from './pages/student/StudentDashboard';
import BookLessonPage from './pages/student/BookLessonPage';
import StudentProfile from './pages/student/StudentProfile';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUserEdit from './pages/admin/AdminUserEdit';
import NotFound from './pages/NotFound';
import LandingPage from './pages/LandingPage';
import HelpPage from './pages/HelpPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import { SearchInstructors } from './pages/SearchInstructors';
import { MockTheoryTest } from './pages/student/MockTheoryTest';
import HazardPerception from './pages/student/HazardPerception';
import { StudentChat } from './pages/student/StudentChat';
import PublicInstructorProfile from './pages/PublicInstructorProfile';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import SecurityPage from './pages/SecurityPage';



// Protected Route Component
const ProtectedRoute: React.FC<{ element: React.ReactNode; roles?: string[] }> = ({
  element,
  roles = [],
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (roles.length > 0 && user && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{element}</>;
};

// Redirect home route to role-based dashboard
const DashboardRedirect: React.FC = () => {
  const { user } = useAuth();
  
  if (user?.role === 'Admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (user?.role === 'Instructor') {
    return <Navigate to="/instructor/dashboard" replace />;
  }
  if (user?.role === 'Student') {
    return <Navigate to="/student/dashboard" replace />;
  }
  return <Navigate to="/login" replace />;
};

// Guest-only Route Component (Redirects authenticated users to their dashboard)
const GuestRoute: React.FC<{ element: React.ReactNode }> = ({ element }) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isAuthenticated && user) {
    if (user.role === 'Admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
    if (user.role === 'Instructor') {
      return <Navigate to="/instructor/dashboard" replace />;
    }
    return <Navigate to="/student/dashboard" replace />;
  }

  return <>{element}</>;
};

const App: React.FC = () => {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "1035515286524-abcdefg.apps.googleusercontent.com";

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <ColorModeProvider>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <SubscriptionProvider>
              <ConfirmProvider>
                <Routes>
                {/* Public Routes */}
                 <Route path="/" element={<LandingPage />} />
                 <Route path="/login" element={<GuestRoute element={<Login />} />} />
                 <Route path="/register" element={<GuestRoute element={<Register />} />} />
                 <Route path="/forgot-password" element={<GuestRoute element={<ForgotPassword />} />} />
                 <Route path="/reset-password" element={<GuestRoute element={<ResetPassword />} />} />
                 <Route path="/confirm-email" element={<ConfirmEmailPage />} />
                 <Route path="/privacy" element={<PrivacyPage />} />
                 <Route path="/terms" element={<TermsPage />} />
                 <Route path="/contact" element={<ContactPage />} />
                 <Route path="/search" element={<MainLayout><SearchInstructors /></MainLayout>} />
                 <Route path="/public/instructor/:idOrSubdomain" element={<PublicInstructorProfile />} />
                 <Route path="/features" element={<FeaturesPage />} />
                 <Route path="/pricing" element={<PricingPage />} />
                 <Route path="/about" element={<AboutPage />} />
                 <Route path="/security" element={<SecurityPage />} />

                {/* Protected Routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <DashboardRedirect />
                        </MainLayout>
                      }
                    />
                  }
                />

                {/* Admin Routes */}
                <Route
                  path="/admin/dashboard"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <AdminDashboard />
                        </MainLayout>
                      }
                      roles={['Admin']}
                    />
                  }
                />
                <Route
                  path="/admin/users/:userId"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <AdminUserEdit />
                        </MainLayout>
                      }
                      roles={['Admin']}
                    />
                  }
                />

                {/* Instructor Routes */}
                <Route
                  path="/instructor/dashboard"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <InstructorDashboard />
                        </MainLayout>
                      }
                      roles={['Instructor']}
                    />
                  }
                />
                <Route
                  path="/instructor/availability/add"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <AddAvailabilityForm />
                        </MainLayout>
                      }
                      roles={['Instructor']}
                    />
                  }
                />
                <Route
                  path="/instructor/students"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <StudentManagement />
                        </MainLayout>
                      }
                      roles={['Instructor']}
                    />
                  }
                />
                <Route
                  path="/instructor/subscribe"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <SubscribePage />
                        </MainLayout>
                      }
                      roles={['Instructor']}
                    />
                  }
                />
                <Route
                  path="/instructor/whatsapp"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <WhatsAppSettings />
                        </MainLayout>
                      }
                      roles={['Instructor']}
                    />
                  }
                />

                {/* Student Routes */}
                <Route
                  path="/student/dashboard"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <StudentDashboard />
                        </MainLayout>
                      }
                      roles={['Student']}
                    />
                  }
                />
                <Route
                  path="/student/book-lesson"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <BookLessonPage />
                        </MainLayout>
                      }
                      roles={['Student']}
                    />
                  }
                />
                <Route
                  path="/student/profile"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <StudentProfile />
                        </MainLayout>
                      }
                      roles={['Student']}
                    />
                  }
                />
                <Route
                  path="/student/theory-test"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <MockTheoryTest />
                        </MainLayout>
                      }
                      roles={['Student']}
                    />
                  }
                />
                <Route
                  path="/student/hazard-test"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <HazardPerception />
                        </MainLayout>
                      }
                      roles={['Student']}
                    />
                  }
                />
                <Route
                  path="/student/chat"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <StudentChat />
                        </MainLayout>
                      }
                      roles={['Student']}
                    />
                  }
                />

                {/* Payment Routes */}
                <Route
                  path="/payment/process"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <PaymentPage />
                        </MainLayout>
                      }
                    />
                  }
                />

                {/* Help Center Routes */}
                <Route
                  path="/help"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <HelpPage />
                        </MainLayout>
                      }
                    />
                  }
                />

                {/* 404 Not Found */}
                <Route path="*" element={<NotFound />} />
                </Routes>
              </ConfirmProvider>
            </SubscriptionProvider>
          </AuthProvider>
        </Router>
      </ColorModeProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
